import { useMemo } from 'react';
import { TextField, ImageField, WrapperField, usePermissions, FunctionField, useTranslate } from 'react-admin';
import { useTheme, useMediaQuery } from '@mui/material';
import { Select1or0Filter } from '@/components/global/filters/filters.jsx';
import { ProductsStatisticList } from '@/components/analytics/statisticList.jsx';
import CustomList from '@/components/global/list/CustomList.jsx';
import { CustomDatagridConfigurable } from '@/components/global/list/CustomDatagridConfigurable.jsx';
import { CollectedField } from '@/components/product/CollectedField.jsx';
import CustomCreateButton from '@/components/global/buttons/CustomCreateButton.jsx';
import { useDrawer } from '@/composables/useDrawer';
import { NoImage } from '@/components/global/fields/NoImage';
import FromToFilterComponent from '@/components/global/filters/FromToFilterComponent.jsx';
import { MarketShare } from '@/components/product/MarketShare.jsx';
import { getProductFilters } from '@/components/product/list/productFilters.jsx';
import { ProductNameField } from '@/components/product/list/ProductNameField.jsx';
import { ProductMoreActions } from '@/components/product/buttons/ProductMoreActions.jsx';
import { ProductListActions } from '@/components/product/buttons/ProductListActions.jsx';
import { ProductBulkActions } from '@/components/product/buttons/ProductBulkActions.jsx';

export { ProductCreate } from '@/components/product/forms/ProductCreate.jsx';
export { ProductEdit } from '@/components/product/forms/ProductEdit.jsx';

export const ProductList = () => {
    const { isLoading, permissions } = usePermissions();
    const { openDrawer } = useDrawer();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const translate = useTranslate();

    const filters = useMemo(() => getProductFilters(isMobile, translate), [isMobile, translate]);

    if (isLoading) return null;

    const adminFilters = (() => {
        const arr = [...filters];
        arr.splice(
            2,
            0,
            <FromToFilterComponent
                key='packagesCount'
                source='analytics->packagesCount_range'
                label={translate('resources.product.fields.collected')}
                forceDesktopOnMobile={true}
                alwaysOn={isMobile}
            />,
            <Select1or0Filter
                key='productWithTags'
                source='productWithTags_map'
                label={translate('resources.product.labels.productWithTags')}
                alwaysOn={isMobile}
            />,
        );
        return arr.filter(Boolean);
    })();

    return (
        <CustomList
            filters={permissions.isAdmin || permissions.isBok ? adminFilters : filters}
            filterDefaultValues={{
                status: 1,
            }}
            actions={<ProductListActions />}
            addLastColumnShadow={true}
            createButton={<CustomCreateButton label={translate('resources.product.labels.addNewProduct')} />}
        >
            <ProductsStatisticList />
            <CustomDatagridConfigurable
                bulkActionButtons={permissions.isAdmin ? <ProductBulkActions /> : false}
                sx={{
                    '& .MuiTableSortLabel-root': {
                        whiteSpace: 'nowrap',
                    },
                }}
                rowClick={id => {
                    openDrawer(id, 'product-show');
                    return false;
                }}
            >
                <FunctionField
                    source='pictureUrl'
                    render={record =>
                        record.pictureUrl ? (
                            <ImageField source='pictureUrl' fullWidth={true} sx={{ wordBreak: 'break-all' }} />
                        ) : (
                            <NoImage
                                sx={{
                                    minWidth: '60px',
                                    height: '60px',
                                    '& svg': {
                                        width: '24px',
                                        height: '24px',
                                    },
                                }}
                            />
                        )
                    }
                />
                <WrapperField label={translate('common.name')} source='name'>
                    <FunctionField render={() => <ProductNameField />} />
                </WrapperField>
                <TextField source='volume' label={translate('resources.product.fields.capacity')} />
                <FunctionField
                    source='material.name'
                    label={translate('resources.product.fields.material')}
                    render={record => (
                        <TextField
                            source='material.name'
                            label={translate('resources.product.fields.material')}
                            onClick={e => {
                                e.stopPropagation();
                                openDrawer(record.materialId, 'material-edit');
                            }}
                            sx={{
                                cursor: 'pointer',
                                '&:hover': {
                                    textDecoration: 'underline',
                                },
                            }}
                        />
                    )}
                />
                <FunctionField
                    source='analytics.packagesCount'
                    label={translate('resources.product.fields.collected')}
                    render={record => <CollectedField record={record} />}
                />
                <FunctionField
                    source='analytics.marketShare'
                    label={translate('resources.product.fields.marketShare')}
                    sortable={true}
                    render={() => <MarketShare />}
                />
                <FunctionField
                    label={translate('common.options')}
                    onClick={e => e.stopPropagation()}
                    render={() => <ProductMoreActions permissions={permissions} />}
                />
            </CustomDatagridConfigurable>
        </CustomList>
    );
};
